import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BlogService } from './blog.service';

@Component({
  selector: 'app-blogs',
  templateUrl: './blogs.component.html',
  styleUrls: ['./blogs.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BlogsComponent implements OnInit {
  blogs: any[] = [];
  featuredBlog: any;
  sideBlogs: any[] = [];
  otherBlogs: any[] = [];

  isLoading = true;
  readonly skeletons = [1, 2, 3, 4, 5, 6];

  private destroyRef = inject(DestroyRef);
  private prefetched = new Set<string>();

  constructor(
    private blogService: BlogService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getBlogs();
  }

  trackByBlog(index: number, blog: any): any {
    return blog?.id ?? blog?.slug ?? index;
  }

  openBlog(blog: any): void {
    if (!blog || !blog.slug) {
      console.warn('Blog slug missing, falling back to ID');
      this.router.navigate(['/blogs', blog.id]);
      return;
    }
    this.router.navigate(['/blogs', blog.slug]);
  }

  /** Warm the cache on hover so the detail page opens instantly */
  prefetchBlog(blog: any): void {
    const slug = blog?.slug;
    if (!slug || this.prefetched.has(slug)) return;
    this.prefetched.add(slug);
    this.blogService.getBlogBySlug(slug).subscribe({
      error: () => this.prefetched.delete(slug)
    });
  }

  getBlogs(): void {
    this.blogService.getAllBlogs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.message === 'Blogs retrieved successfully!') {
            // copy before reversing — never mutate the cached array
            this.blogs = [...(response.data || [])].reverse(); // latest first
            this.arrangeBlogs();
          } else {
            this.blogs = [];
            alert('Failed to fetch blogs: ' + response?.message);
          }
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching blogs:', error);
          this.blogs = [];
          this.isLoading = false;
          this.cdr.markForCheck();
          alert('An error occurred while fetching blogs.');
        }
      });
  }

  arrangeBlogs(): void {
    this.featuredBlog = this.blogs[0];
    this.sideBlogs = this.blogs.slice(1, 4);
    this.otherBlogs = this.blogs.slice(4);
  }

  scrollToBlogs(): void {
    const calendarElement = document.getElementById('calendar-section');
    if (calendarElement) {
      calendarElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
